import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, ClipboardList, FileText, Vote, AlertTriangle, X, BarChart3 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMBLEM = "/ginius-emblem.png";

export default function RaccoltaPublic() {
  const { token } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  // Iter122ji · Risultati (solo se sondaggio delibera scaduto)
  const [risultati, setRisultati] = useState(null);

  useEffect(() => {
    axios.get(`${API}/raccolta/${token}`)
      .then((r) => { setData(r.data); setVals(r.data.risposte || {}); })
      .catch((e) => setError(e.response?.data?.detail || "Link non valido o non più attivo."))
      .finally(() => setLoading(false));
  }, [token]);

  // Iter122ji · Se sondaggio delibera scaduto → carica anche i risultati aggregati
  useEffect(() => {
    if (!data || data.tipo !== "sondaggio_delibera" || !data.scaduta) return;
    axios.get(`${API}/raccolta/${token}/risultati`)
      .then((r) => setRisultati(r.data))
      .catch(() => setRisultati(null));
  }, [data, token]);

  const setV = (id, v) => setVals((s) => ({ ...s, [id]: v }));

  const invia = async () => {
    setBusy(true);
    try {
      const { data: res } = await axios.post(`${API}/raccolta/${token}`, { risposte: vals });
      setDone(res);
    } catch (e) {
      setDone({ ok: false, message: e.response?.data?.detail || "Invio non riuscito. Riprova." });
    } finally { setBusy(false); }
  };

  // Iter122ji · X di chiusura: se l'utente è loggato torna alla lista, altrimenti prova a chiudere tab
  const chiudi = () => {
    try {
      const hasToken = localStorage.getItem("izzo_token");
      if (hasToken) {
        nav("/mie-raccolte");
        return;
      }
    } catch {}
    if (window.history.length > 1) window.history.back();
    else window.close();
  };

  const fmt = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); } catch { return iso; }
  };

  const field = (c, readOnly = false) => {
    const common = "w-full h-12 border-2 border-slate-300 rounded-lg px-3 disabled:bg-slate-50 disabled:text-slate-500";
    const val = vals[c.id] || "";
    if (c.tipo === "numero") return <input data-testid={`campo-${c.id}`} type="number" disabled={readOnly} className={common} value={val} onChange={(e) => setV(c.id, e.target.value)} />;
    if (c.tipo === "data") return <input data-testid={`campo-${c.id}`} type="date" disabled={readOnly} className={common} value={val} onChange={(e) => setV(c.id, e.target.value)} />;
    if (c.tipo === "scelta") return (
      <select data-testid={`campo-${c.id}`} disabled={readOnly} className={common + " bg-white"} value={val} onChange={(e) => setV(c.id, e.target.value)}>
        <option value="">— Seleziona —</option>
        {(c.opzioni || []).map((o, i) => <option key={i} value={o}>{o}</option>)}
      </select>
    );
    if (c.tipo === "sino") return (
      <select data-testid={`campo-${c.id}`} disabled={readOnly} className={common + " bg-white"} value={val} onChange={(e) => setV(c.id, e.target.value)}>
        <option value="">— Seleziona —</option><option value="Sì">Sì</option><option value="No">No</option>
      </select>
    );
    return <input data-testid={`campo-${c.id}`} type="text" disabled={readOnly} className={common} value={val} onChange={(e) => setV(c.id, e.target.value)} />;
  };

  const isOneShotDone = !!data?.gia_risposto;
  const isSondaggio = data?.tipo === "sondaggio_delibera";
  const showResults = risultati && isSondaggio && data?.scaduta;

  return (
    <div className="min-h-screen bg-[#1A1818] flex flex-col items-center px-4 py-8">
      <div className="flex items-center gap-3 mb-6">
        <img src={EMBLEM} alt="Ginius" className="w-12 h-12 rounded-xl ring-2 ring-[#D4AF5A]/70" />
        <div className="leading-none">
          <div className="font-brand font-bold text-3xl text-brand" style={{ WebkitTextStroke: "0.5px #fff" }}>Ginius<span className="text-[#D4AF5A]">.</span></div>
          {data?.titolare?.ragione_sociale && <div className="text-xs text-[#E7C9A0]" data-testid="public-header-studio">{data.titolare.ragione_sociale}</div>}
        </div>
      </div>
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl p-6 relative" data-testid="raccolta-public-card">
        {/* Iter122ji · X di chiusura */}
        <button data-testid="raccolta-chiudi-btn" onClick={chiudi}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 hover:text-slate-900"
          aria-label="Chiudi">
          <X className="w-5 h-5" />
        </button>

        {loading && <div className="flex items-center justify-center py-10 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Caricamento…</div>}
        {!loading && error && <div className="text-center py-8 text-red-700 font-semibold" data-testid="raccolta-error">{error}</div>}

        {!loading && data && !done && (
          <>
            {/* Header sondaggio delibera */}
            {isSondaggio ? (
              <div className="flex items-center gap-2 text-brand font-bold text-lg mb-1 pr-8" data-testid="sondaggio-header">
                <Vote className="w-6 h-6" /> Sondaggio delibera condominiale
              </div>
            ) : (
              <div className="flex items-center gap-2 text-brand font-bold text-lg mb-1 pr-8">
                <ClipboardList className="w-6 h-6" /> {data.titolo}
              </div>
            )}
            {data.condominio_nome && <p className="text-sm font-semibold text-slate-700 mb-2">Condominio: {data.condominio_nome}</p>}
            {data.nome && <p className="text-sm text-slate-500 mb-1">Come: <b>{data.nome}</b></p>}

            {/* Riepilogo preventivo per sondaggi delibera */}
            {data.sondaggio_meta && (
              <div className="mb-4 p-3 rounded-lg border-2 border-brand/30 bg-[#FBEEF3]/50" data-testid="sondaggio-riepilogo">
                <div className="text-xs font-bold uppercase tracking-wide text-brand mb-1">Preventivo da approvare</div>
                <div className="text-base font-bold text-slate-800">{data.sondaggio_meta.oggetto}</div>
                <div className="text-sm text-slate-700 mt-1">
                  <b>Fornitore:</b> {data.sondaggio_meta.fornitore_nome || "—"}<br/>
                  <b>Importo:</b> {data.sondaggio_meta.importo || "—"}
                  {data.sondaggio_meta.numero_preventivo && <><br/><b>Riferimento:</b> {data.sondaggio_meta.numero_preventivo}</>}
                </div>
                {data.allegato_url && (
                  <a href={data.allegato_url} target="_blank" rel="noreferrer"
                    data-testid="sondaggio-pdf-link"
                    className="inline-flex items-center gap-1 mt-2 text-sm font-semibold text-brand hover:underline">
                    <FileText className="w-4 h-4" /> Visualizza PDF preventivo
                  </a>
                )}
              </div>
            )}

            {data.data_scadenza && (
              <div className={`mb-4 p-2 rounded-lg text-sm border ${data.scaduta ? "bg-red-50 border-red-200 text-red-800" : "bg-amber-50 border-amber-200 text-amber-900"}`} data-testid="sondaggio-scadenza">
                {data.scaduta ? (
                  <span className="flex items-center gap-1 font-bold"><AlertTriangle className="w-4 h-4" /> Termine di voto scaduto il {data.data_scadenza}</span>
                ) : (
                  <>Termine di voto: <b>{data.data_scadenza}</b></>
                )}
              </div>
            )}

            {data.descrizione && <p className="text-sm text-slate-600 mb-4 whitespace-pre-wrap">{data.descrizione}</p>}

            {/* Iter122ji · ONE-SHOT: se già risposto, mostra riepilogo readonly senza form + data */}
            {isOneShotDone ? (
              <div className="mb-4 rounded-lg border-2 border-emerald-300 bg-emerald-50 p-3" data-testid="gia-risposto-banner">
                <div className="flex items-center gap-2 text-emerald-800 font-bold">
                  <CheckCircle2 className="w-5 h-5" /> {isSondaggio ? "Voto già espresso" : "Dati già inviati"}
                </div>
                {data.risposto_at && (
                  <div className="text-sm text-emerald-900 mt-1">
                    {isSondaggio ? "Hai votato il" : "Hai risposto il"} <b>{fmt(data.risposto_at)}</b>.
                  </div>
                )}
                <div className="text-xs text-emerald-800 mt-1">
                  {isSondaggio ? "La tua risposta è definitiva e non modificabile." : (
                    <>
                      Le tue risposte sono state acquisite e non sono modificabili.
                      {" "}Puoi consultare/aggiornare i tuoi dati nella sezione{" "}
                      {/* Iter122jk · link cliccabile se l'utente è loggato (ha token in localStorage) */}
                      {(() => {
                        let logged = false;
                        try { logged = !!localStorage.getItem("izzo_token"); } catch { logged = false; }
                        return logged ? (
                          <a
                            data-testid="link-mio-immobile"
                            href="/proprieta"
                            onClick={(e) => { e.preventDefault(); nav("/proprieta"); }}
                            className="font-bold text-emerald-800 underline underline-offset-2 hover:text-emerald-900"
                          >
                            "I dati del mio immobile"
                          </a>
                        ) : (
                          <b>"I dati del mio immobile"</b>
                        );
                      })()}.
                    </>
                  )}
                </div>
              </div>
            ) : null}

            <div className="space-y-4">
              {(data.campi || []).map((c) => (
                <div key={c.id}>
                  <label className="block text-sm font-bold text-slate-700 mb-1">{c.etichetta} {c.obbligatorio && !isOneShotDone && <span className="text-red-600">*</span>}</label>
                  {field(c, isOneShotDone || data.scaduta)}
                </div>
              ))}
            </div>

            {/* Iter122ji · Risultati (barre percentuali) — SOLO se sondaggio delibera scaduto */}
            {showResults && (
              <div className="mt-6 p-4 rounded-xl border-2 border-indigo-200 bg-indigo-50/50" data-testid="sondaggio-risultati">
                <div className="flex items-center gap-2 text-indigo-800 font-bold text-base mb-2">
                  <BarChart3 className="w-5 h-5" /> Esito del sondaggio
                </div>
                {/* Iter122k36 · Badge esito calcolato con regola "teste" (maggioranza aventi diritto) */}
                {risultati.esito_label && (
                  <div className={`mb-3 p-2 rounded-lg border-2 text-center ${
                    risultati.esito_label === "APPROVATO" ? "border-emerald-500 bg-emerald-50" :
                    risultati.esito_label === "NON APPROVATO" ? "border-red-500 bg-red-50" :
                    "border-amber-500 bg-amber-50"}`}
                    data-testid="sondaggio-esito-label">
                    <div className={`font-extrabold text-lg ${
                      risultati.esito_label === "APPROVATO" ? "text-emerald-800" :
                      risultati.esito_label === "NON APPROVATO" ? "text-red-800" :
                      "text-amber-800"}`}>{risultati.esito_label}</div>
                    {risultati.soglia_maggioranza != null && (
                      <div className="text-[11px] text-slate-600 mt-0.5">
                        Servono almeno {risultati.soglia_maggioranza}/{risultati.aventi_diritto} voti favorevoli (maggioranza aventi diritto)
                      </div>
                    )}
                  </div>
                )}
                <div className="text-xs text-slate-600 mb-3">
                  <b>{risultati.totale_votanti}</b> voti espressi su <b>{risultati.aventi_diritto}</b> aventi diritto
                </div>
                <div className="space-y-2">
                  {[
                    { key: "Approvo", color: "bg-emerald-500", txt: "text-emerald-800" },
                    { key: "Non approvo", color: "bg-red-500", txt: "text-red-800" },
                    { key: "Astenuto", color: "bg-slate-400", txt: "text-slate-800" },
                  ].map(({ key, color, txt }) => {
                    const e = risultati.esito?.[key] || { count: 0, pct: 0 };
                    return (
                      <div key={key} data-testid={`risultato-${key.toLowerCase().replace(/\s/g,'-')}`}>
                        <div className={`flex justify-between text-xs font-bold ${txt}`}>
                          <span>{key}</span>
                          <span>{e.count} · {e.pct}%</span>
                        </div>
                        <div className="w-full h-3 bg-white rounded-full border border-slate-200 overflow-hidden mt-0.5">
                          <div className={`h-full ${color} transition-all`} style={{ width: `${Math.min(100, e.pct)}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[11px] text-slate-500 mt-3 italic">Esito consultivo. La delibera formale è quella dell'assemblea condominiale.</p>
              </div>
            )}

            {!isOneShotDone && (
              <button data-testid="raccolta-invia-btn" disabled={busy || data.scaduta} onClick={invia}
                className="mt-6 w-full h-14 rounded-xl bg-green-700 hover:bg-green-800 text-white font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-60">
                <CheckCircle2 className="w-6 h-6" /> {busy ? "Invio…" : (data.scaduta ? "Voto chiuso" : (isSondaggio ? "Invia il tuo voto (definitivo)" : "Invia i dati (definitivo)"))}
              </button>
            )}
            {isOneShotDone && (
              <button data-testid="raccolta-torna-btn" onClick={chiudi}
                className="mt-6 w-full h-12 rounded-xl bg-slate-700 hover:bg-slate-800 text-white font-semibold flex items-center justify-center gap-2">
                Torna alla lista
              </button>
            )}
          </>
        )}

        {done && (
          <div className="text-center py-8" data-testid="raccolta-done">
            <CheckCircle2 className={`w-16 h-16 mx-auto mb-3 ${done.ok ? "text-green-600" : "text-amber-500"}`} />
            <p className="text-lg font-semibold text-slate-800">{done.message}</p>
            <p className="text-sm text-slate-500 mt-2">La risposta è definitiva. Puoi chiudere questa pagina.</p>
            <button data-testid="done-chiudi-btn" onClick={chiudi}
              className="mt-4 h-11 px-6 rounded-lg bg-slate-700 hover:bg-slate-800 text-white font-semibold">
              Chiudi
            </button>
          </div>
        )}
      </div>
      <p className="text-xs text-slate-500 mt-6" data-testid="public-footer-studio">{data?.titolare?.ragione_sociale || "Ginius"} · Gestione manutenzioni</p>
    </div>
  );
}
